import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Alert, Card, DISCLAIMER_TEXT, Field } from '../components/ui';
import PasswordField from '../components/PasswordField';

export default function Register() {
  const { register } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'error', 'Could not create the account.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-8">
      <h1 className="mb-6 text-center text-2xl font-bold">
        <span aria-hidden="true">🔥</span> FIRE Tracker
      </h1>

      <Card title="Create your account">
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <Alert tone="critical">{error.message}</Alert>}

          <Field label="Email" error={error?.fieldError('email')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>

          <PasswordField
            value={password}
            onChange={setPassword}
            email={email}
            error={error?.fieldError('password')}
          />

          {/* §12: the disclaimer is shown at registration, and acknowledging it
              is enforced by the API rather than being decorative. */}
          <div className="rounded-md border border-black/10 bg-surface-muted p-3 text-xs dark:border-white/10 dark:bg-surface-dark">
            <p className="mb-2 font-semibold">Before you start</p>
            <p className="text-ink-muted dark:text-ink-inverse-muted">{DISCLAIMER_TEXT}</p>
            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                required
              />
              <span>I understand this is a modelling tool, not financial advice.</span>
            </label>
          </div>

          <button type="submit" className="btn-primary w-full" disabled={busy || !accepted}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>

          <p className="text-center text-sm">
            Already have an account?{' '}
            <Link to="/login" className="underline">
              Sign in
            </Link>
          </p>
        </form>
      </Card>

      <p className="mt-4 text-center text-xs text-ink-muted dark:text-ink-inverse-muted">
        Never enter bank passwords, PINs, card numbers, PAN or Aadhaar anywhere in this app. It
        does not store them, and will reject them if you try.
      </p>
    </div>
  );
}
